import type { Permission } from '@teachino/shared-util';

/**
 * TE-2506 features that ignore license permission for non-paid users.
 *
 * For these features, free/trial users only get access during an active trial or
 * the legacy grace window — not because their license lists the feature.
 *
 * Why only student_access and ipi_usage_unlimited?
 * - student_access is not on the free plan; trial access must come from the soft-paywall window.
 * - ipi_usage_unlimited is not even on the trial plan (paid Pro only), so the same rule applies.
 *
 * Other Pro features (presentation_mode, calendar_*, collaboration_*, …) are not listed here
 * because they use different helpers with the same user-facing goal (paid, trial, or grace):
 * - trial-expiry-gated features → trial-expiry-gated-features.ts (blocks stale trial license flags)
 * - e.g. collaboration_teamteaching → canUseProFeature() in soft-paywall.ts
 *
 * This list is not “all paywalled features” — it is the subset that must never rely on
 * hasPermission alone for non-paid users.
 */
export const PAYWALL_GATED_FEATURES = [
  'student_access',
  'ipi_usage_unlimited',
] as const satisfies readonly Permission[];

export type PaywallGatedFeature = (typeof PAYWALL_GATED_FEATURES)[number];

export function isPaywallGatedFeature(
  feature: Permission,
): feature is PaywallGatedFeature {
  return (PAYWALL_GATED_FEATURES as readonly Permission[]).includes(feature);
}
